import React, { useState, useEffect, useCallback } from 'react';
import { Award, Star, Trophy, Target, Flame, Clock, Users, Heart, Zap, Crown } from 'lucide-react';
import { toast } from 'sonner';

// Badge definitions
const BADGES = {
  // Meeting Attendance Badges
  first_meeting: {
    id: 'first_meeting',
    name: 'First Steps',
    description: 'Attended your first verified meeting',
    icon: Star,
    color: 'bg-blue-500',
    requirement: { type: 'meetings', count: 1 }
  },
  meetings_5: {
    id: 'meetings_5',
    name: 'Getting Started',
    description: 'Attended 5 verified meetings',
    icon: Target,
    color: 'bg-green-500',
    requirement: { type: 'meetings', count: 5 }
  },
  meetings_10: {
    id: 'meetings_10',
    name: 'Committed',
    description: 'Attended 10 verified meetings',
    icon: Award,
    color: 'bg-purple-500',
    requirement: { type: 'meetings', count: 10 }
  },
  meetings_25: {
    id: 'meetings_25',
    name: 'Dedicated',
    description: 'Attended 25 verified meetings',
    icon: Trophy,
    color: 'bg-amber-500',
    requirement: { type: 'meetings', count: 25 }
  },
  meetings_50: {
    id: 'meetings_50',
    name: 'Champion',
    description: 'Attended 50 verified meetings',
    icon: Crown,
    color: 'bg-red-500',
    requirement: { type: 'meetings', count: 50 }
  },
  meetings_100: {
    id: 'meetings_100',
    name: 'Legend',
    description: 'Attended 100 verified meetings',
    icon: Zap,
    color: 'bg-gradient-to-r from-purple-500 to-pink-500',
    requirement: { type: 'meetings', count: 100 }
  },
  
  // Streak Badges
  streak_7: {
    id: 'streak_7',
    name: '7-Day Streak',
    description: 'Attended meetings 7 days in a row',
    icon: Flame,
    color: 'bg-orange-500',
    requirement: { type: 'streak', count: 7 }
  },
  streak_30: {
    id: 'streak_30',
    name: '30-Day Warrior',
    description: 'Attended meetings 30 days in a row',
    icon: Flame,
    color: 'bg-red-600',
    requirement: { type: 'streak', count: 30 }
  },
  streak_90: {
    id: 'streak_90',
    name: '90-Day Hero',
    description: 'Attended meetings 90 days in a row',
    icon: Flame,
    color: 'bg-gradient-to-r from-red-500 to-orange-500',
    requirement: { type: 'streak', count: 90 }
  },
  
  // Sobriety Milestones
  sober_7: {
    id: 'sober_7',
    name: 'One Week Clean',
    description: '7 days of sobriety',
    icon: Clock,
    color: 'bg-teal-500',
    requirement: { type: 'sobriety_days', count: 7 }
  },
  sober_30: {
    id: 'sober_30',
    name: 'One Month Clean',
    description: '30 days of sobriety',
    icon: Clock,
    color: 'bg-cyan-500',
    requirement: { type: 'sobriety_days', count: 30 }
  },
  sober_90: {
    id: 'sober_90',
    name: '90 Days Clean',
    description: '90 days of sobriety',
    icon: Award,
    color: 'bg-emerald-500',
    requirement: { type: 'sobriety_days', count: 90 }
  },
  sober_180: {
    id: 'sober_180',
    name: '6 Months Clean',
    description: '180 days of sobriety',
    icon: Trophy,
    color: 'bg-blue-600',
    requirement: { type: 'sobriety_days', count: 180 }
  },
  sober_365: {
    id: 'sober_365',
    name: 'One Year Clean',
    description: '365 days of sobriety',
    icon: Crown,
    color: 'bg-gradient-to-r from-amber-500 to-yellow-400',
    requirement: { type: 'sobriety_days', count: 365 }
  },
  
  // Journal Badges
  journal_first: {
    id: 'journal_first',
    name: 'First Reflection',
    description: 'Wrote your first journal entry',
    icon: Heart,
    color: 'bg-pink-500',
    requirement: { type: 'journals', count: 1 }
  },
  journal_30: {
    id: 'journal_30',
    name: 'Deep Thinker',
    description: 'Wrote 30 journal entries',
    icon: Heart,
    color: 'bg-rose-500',
    requirement: { type: 'journals', count: 30 }
  },
  
  // Group Chat Badges
  group_first: {
    id: 'group_first',
    name: 'Community Member',
    description: 'Sent your first group chat message',
    icon: Users,
    color: 'bg-indigo-500',
    requirement: { type: 'group_messages', count: 1 }
  },
  group_helper: {
    id: 'group_helper',
    name: 'Helpful Peer',
    description: 'Sent 50 group chat messages',
    icon: Users,
    color: 'bg-violet-500',
    requirement: { type: 'group_messages', count: 50 }
  }
};

// Badge display component
function BadgeCard({ badge, earned, earnedAt, size = 'normal' }) {
  const IconComponent = badge.icon;
  const isLarge = size === 'large';
  
  return (
    <div 
      className={`relative ${isLarge ? 'p-4' : 'p-3'} rounded-xl border transition-all ${
        earned 
          ? 'bg-white border-zinc-200 shadow-sm' 
          : 'bg-zinc-50 border-zinc-100 opacity-50'
      }`}
      data-testid={`badge-${badge.id}`}
    >
      <div className="flex items-center gap-3">
        <div className={`${isLarge ? 'w-12 h-12' : 'w-10 h-10'} rounded-full ${badge.color} flex items-center justify-center ${!earned && 'grayscale'}`}>
          <IconComponent className={`${isLarge ? 'w-6 h-6' : 'w-5 h-5'} text-white`} />
        </div>
        <div className="flex-1">
          <h4 className={`font-semibold text-zinc-900 ${isLarge ? 'text-sm' : 'text-xs'}`}>
            {badge.name}
          </h4>
          <p className={`text-zinc-500 ${isLarge ? 'text-xs' : 'text-[10px]'}`}>
            {badge.description}
          </p>
          {earned && earnedAt && (
            <p className="text-[10px] text-green-600 mt-1">
              Earned {new Date(earnedAt).toLocaleDateString()}
            </p>
          )}
        </div>
      </div>
      {!earned && (
        <div className="absolute top-2 right-2">
          <span className="text-[10px] bg-zinc-200 text-zinc-600 px-1.5 py-0.5 rounded">
            Locked
          </span>
        </div>
      )}
    </div>
  );
}

// Badges grid component
function BadgesGrid({ earnedBadges = [], showAll = true }) {
  const allBadges = Object.values(BADGES);
  const badgesToShow = showAll ? allBadges : allBadges.filter(b => earnedBadges.some(e => e.badge_id === b.id));
  
  return (
    <div className="grid grid-cols-2 gap-3">
      {badgesToShow.map((badge) => {
        const earned = earnedBadges.find(e => e.badge_id === badge.id);
        return (
          <BadgeCard 
            key={badge.id} 
            badge={badge} 
            earned={!!earned}
            earnedAt={earned?.earned_at}
          />
        );
      })}
    </div>
  );
}

// Mini badge display for dashboard
function BadgesMini({ earnedBadges = [], limit = 4 }) {
  const recentBadges = earnedBadges
    .sort((a, b) => new Date(b.earned_at) - new Date(a.earned_at))
    .slice(0, limit);
  
  if (recentBadges.length === 0) {
    return (
      <div className="text-center py-4">
        <p className="text-zinc-500 text-xs">No badges earned yet. Keep attending meetings!</p>
      </div>
    );
  }
  
  return (
    <div className="flex items-center gap-2 overflow-x-auto pb-2">
      {recentBadges.map((earned) => {
        const badge = BADGES[earned.badge_id];
        if (!badge) return null;
        const IconComponent = badge.icon;
        return (
          <div 
            key={earned.badge_id}
            className={`flex-shrink-0 w-10 h-10 rounded-full ${badge.color} flex items-center justify-center`}
            title={badge.name}
          >
            <IconComponent className="w-5 h-5 text-white" />
          </div>
        );
      })}
      {earnedBadges.length > limit && (
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-zinc-200 flex items-center justify-center">
          <span className="text-xs font-bold text-zinc-600">+{earnedBadges.length - limit}</span>
        </div>
      )}
    </div>
  );
}

// Hook to check and award badges
function useBadges() {
  const [earnedBadges, setEarnedBadges] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchBadges = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/badges`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setEarnedBadges(data.badges || []);
      }
    } catch (error) {
      console.error('Error fetching badges:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const checkAndAwardBadges = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/badges/check`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.new_badges && data.new_badges.length > 0) {
          // Show toast for each new badge
          data.new_badges.forEach((badgeId) => {
            const badge = BADGES[badgeId];
            if (badge) {
              toast.success(`🏆 New Badge Earned: ${badge.name}!`, {
                description: badge.description,
                duration: 5000
              });
            }
          });
          // Refresh badges
          fetchBadges();
        }
      }
    } catch (error) {
      console.error('Error checking badges:', error);
    }
  }, [fetchBadges]);

  useEffect(() => {
    fetchBadges();
  }, [fetchBadges]);

  return { earnedBadges, loading, fetchBadges, checkAndAwardBadges };
}

export { BADGES, BadgeCard, BadgesGrid, BadgesMini, useBadges };
